import { UrlString } from 'types/UrlString';

import { DateString } from '../../../types/DateString';

import type { ExerciseId } from './exercises.branded';

export type { ExerciseId } from './exercises.branded';
export { toExerciseId } from './exercises.branded';

export enum ExerciseFileTypes {
  hint = 'p',
  answer = 'o',
  question = 't',
  attachment = 'a',
}

export type ExerciseUserMark = 'easy' | 'hard' | 'explain';

export type ExerciseFiles = Record<ExerciseFileTypes, UrlString[]>;

export type ExerciseLevel = 'PP' | 'PR';

export type ExerciseResourceCategory = 'youtube' | 'wiki' | 'other' | 'zpe';

export type ExerciseForm =
  | 'OTHER'
  | 'TEST'
  | 'MULTI_TEST'
  | 'GROUPED_MULTI_TEST'
  | 'TRUE_FALSE'
  | 'MATCHING'
  | 'ORDERING'
  | 'GAP_FILL'
  | 'SHORT_ANSWER';
// TEST, "Test (ABCD)"
// MULTI_TEST, "Test wielokrotnego wyboru"
// GROUPED_MULTI_TEST, "Test wielokrotnego wyboru z grupami"
// TRUE_FALSE, "Prawda / fałsz"
// MATCHING, "Dopasowanie"
// ORDERING, "Uporządkowanie"
// GAP_FILL, "Uzupełnij zdanie"
// SHORT_ANSWER, "Krótka odpowiedź";

export type ExerciseFileDto = {
  uuid: string;
  png_file: string;
  cropped_png_file: string;
  file_type: ExerciseFileTypes;
  file_index: number;
};

export type ExerciseFile = {
  uuid: string;
  pngFile: string;
  croppedPngFile: string;
  fileType: ExerciseFileTypes;
  fileIndex: number;
};

export type ExerciseAnswerDto = {
  id: number;
  label: string;
  /** Present for grouped forms such as TRUE_FALSE and GROUPED_MULTI_TEST — choices share the same group index. */
  group?: number;
};

export type ExerciseAnswer = ExerciseAnswerDto;

/** OpenAPI `ExerciseUserData`. */
export type ExerciseUserDataDto = {
  id: number;
  note?: string;
  drawing_data?: string;
  drawing_encoding_type?: string;
};

export type ExerciseUserData = {
  id: number;
  note: string | null;
  drawingData: string | null;
  drawingEncodingType: string | null;
};

/** OpenAPI `ExerciseUserDataUpdate` — omit fields to leave them unchanged on the server. */
export type ExerciseUserDataUpdateDto = {
  note?: string;
  drawing_data?: string;
  drawing_encoding_type?: string;
};

export type ExerciseUserDataUpdate = {
  note?: string;
  drawingData?: string;
  drawingEncodingType?: string;
};

/** OpenAPI `ExerciseMarkRequest`. */
export type ExerciseMarkRequestDto = {
  mark: ExerciseUserMark;
};

export type ExerciseMarkRequest = {
  mark: ExerciseUserMark;
};

/** OpenAPI `ExerciseUserMark` object (mark write response / nested on UserExercise). */
export type ExerciseUserMarkDto = {
  id: number;
  created_at: DateString | null;
  mark: ExerciseUserMark | '' | null;
  is_done?: string | null;
  updated_at: DateString | null;
  exercise_index: number;
  exercise_section_name: string;
  exercise_subsection_name?: string;
  exercise_subject_name: string;
  exercise_uuid: string;
};

export type ExerciseUserMarkDetails = {
  id: number;
  createdAt: Date | null;
  mark: ExerciseUserMark | null;
  isDone: string | null;
  updatedAt: Date | null;
  exerciseIndex: number;
  exerciseSectionName: string;
  exerciseSubsectionName: string | null;
  exerciseSubjectName: string;
  exerciseUuid: string;
};

export type ExerciseDto = {
  id?: number;
  uuid: string;
  subject: number;
  section: number;
  subsection: number;
  tags: [number];
  level: ExerciseLevel;
  date: string;
  source: string;
  files: ExerciseFileDto[];
  import_file_identifier: string;
  index: number;
  external_sources: {
    id: number;
    category: ExerciseResourceCategory;
    created_at: string;
    description: string;
    index: number;
    is_main: boolean;
    is_section_related: boolean;
    updated_at: string;
    url: string;
    user_rating: string;
  }[];
  audio: string;
  video: string;
  answers: ExerciseAnswerDto[];
  global_id: string;
  user_data: ExerciseUserDataDto | null;
  user_mark: ExerciseUserMarkDto | null;
  form: ExerciseForm;
};

export type Exercise = {
  id: ExerciseId;
  subject: number;
  section: number;
  subsection: number;
  tags: [number];
  level: ExerciseLevel;
  date: Date;
  source: string;
  files: ExerciseFiles;
  importFileIdentifier: string;
  index: number;
  externalSources: {
    id: number;
    category: ExerciseResourceCategory;
    createdAt: Date;
    description: string;
    index: number;
    isMain: boolean;
    isSectionRelated: boolean;
    updatedAt: Date;
    url: string;
    userRating: string;
  }[];
  audio: string;
  video: string;
  answers: ExerciseAnswer[];
  // TODO remove if get rid of GQL
  globalId: string;
  userData: ExerciseUserData | null;
  userMark: null | ExerciseUserMark;
  form: ExerciseForm;
};

/** GraphQL-parity shape for classic exercises store / ExerciseMain. */
export type ClassicExerciseFile = {
  croppedPngFile: string;
  fileType: ExerciseFileTypes;
  fileIndex: number;
};

export type ClassicExercise = {
  id: string;
  importFileIdentifier: string;
  audio: string | null;
  index: number;
  section: { id: number; name: string };
  subsection: { id: number; name: string };
  externalSources: {
    id: number;
    category: ExerciseResourceCategory;
    description: string;
    url: string;
    isMain: boolean;
  }[];
  userMark: { id: number; mark: ExerciseUserMark } | null;
  files: ClassicExerciseFile[];
  userData: ExerciseUserData | null;
};

export type ClassicExerciseSectionNames = {
  sectionName?: string;
  subsectionName?: string;
};

/** OpenAPI `ExerciseList` — browse / sheets / by-indexes row. */
export type ExerciseListRowDto = {
  uuid: string;
  index: number | null;
  import_file_identifier: string;
};

export type ExerciseListRow = {
  /** Plain UUID; kept as `id` for classic store / exerciseIdForRestApi compatibility. */
  id: string;
  uuid: string;
  index: number | null;
  importFileIdentifier: string;
};

export type ExerciseListQuery = {
  subject: number;
  section?: number | null;
  subsection?: number | null;
  tags?: (number | string)[] | null;
  curriculum?: string[] | null;
  marks?: string[] | null;
  indexes?: number[] | null;
  orderBy?: string | string[] | null;
  limit?: number;
  offset?: number;
};

export type ExerciseListPage = {
  count: number;
  exercises: ExerciseListRow[];
};

/** OpenAPI `ExerciseTag` / `ExerciseTagCategory`. */
export type ExerciseTagDto = {
  id: number;
  name: string;
  exercises_count: number;
};

export type ExerciseTagCategoryDto = {
  id: number;
  name: string;
  index?: number;
  tags: ExerciseTagDto[];
};

export type ExerciseTag = {
  id: number;
  name: string;
  exercisesCount: number;
};

export type ExerciseTagCategory = {
  id: number;
  name: string;
  index?: number;
  tags: ExerciseTag[];
};

export type TagCategoriesQuery = {
  subject: number;
  section?: number | null;
  subsection?: number | null;
  tags?: (number | string)[] | null;
  curriculum?: string[] | null;
};

/** OpenAPI `ExercisePrint`. */
export type ExercisePrintDto = {
  uuid: string;
  index: number | null;
  section: { id: number; name: string } | null;
  subsection: { id: number; name: string } | null;
  files: ExerciseFileDto[];
};

export type ExercisePrint = {
  id: string;
  index: number | null;
  section: { id: number; name: string } | null;
  subsection: { id: number; name: string } | null;
  files: { croppedPngFile: string; fileType: string }[];
};

export type SheetsCatalog = Record<string, number[]>;

export type SheetsExercisesQuery = {
  subject: number;
  curriculum: string[];
  year: number;
  month: number;
};
